<template>
  <div :class="{ 'blog-search_active': isMobileOpen }" class="blog-search">
    <form class="blog-search-form">
      <span @click="handlerMobileOpen">
        <svg class="blog-search-form__icon"><use xlink:href="#svg-blog-search"></use></svg>
      </span>
      <input
        v-if="screenWidth >= 768 || isMobileOpen"
        v-model="searchString"
        :placeholder="messages.searchBy"
        type="text"
        class="blog-search-form__input"
        @input="closeResponse"
        @keypress.enter.prevent="handlerGoToSearchPage"
      />
      <button
        v-if="screenWidth >= 768"
        type="button"
        class="blog-search-form__button"
        @click="handlerGoToSearchPage"
      >
        {{ messages.find }}
      </button>
      <span v-else-if="isMobileOpen" class="blog-search-form__close" @click="handlerClose">
        <svg>
          <use xlink:href="#svg-blog-close"></use>
        </svg>
      </span>
    </form>
    <div v-if="items" class="blog-search-response" @wheel.prevent>
      <DefScrollbar v-if="items.length" class="blog-search-items">
        <BlogSearchItem v-for="item in items" :key="item.id" :result="item" />
      </DefScrollbar>
      <p v-else-if="responseMessages" class="blog-search-response__text">
        {{ responseMessages.nothingFound }} <b>"{{ searchString }}"</b>
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue';
import useScreenWidth from '@use/useScreenWidth';
import BlogSearchItem from '@components/Blog/Header/BlogSearchItem.vue';
import { useResultsStore } from '@/storePinia/stores/results';
import DefScrollbar from '@components/Def/DefScrollbar/DefScrollbar.vue';

const screenWidth = useScreenWidth();

const resultsStore = useResultsStore();

const messages = computed(() => resultsStore.messages?.blog);

const searchString = ref('');
const isMobileOpen = ref(false);
const isOpenResponse = ref(false);
const response = reactive({});
const timeOutId = ref(null);

const items = computed(() => response.value?.blog?.items);
const responseMessages = computed(() => response.value?.messages);

const search = async () => {
  try {
    const params = {
      ...resultsStore.getRequestLang,
      q: searchString.value,
      ajax: 'y',
      action: 'blogSearch',
    };
    const { status, data } = await axios.get('/ajax/', { params });
    if (status !== 200) throw `Bad request response status: ${status}`;

    if (data) {
      response.value = data;
      isOpenResponse.value = true;
    }
  } catch (err) {
    console.error(err);
  }
};

const closeResponse = () => {
  clearTimeout(timeOutId.value);
  if (searchString.value.length >= 3) {
    timeOutId.value = setTimeout(search, 100);
  }
};
const handlerClose = () => {
  isMobileOpen.value = false;
  searchString.value = '';
  response.value = {};
};
const handlerAnotherClick = ({ target }) => {
  if (!target.closest('.blog-search')) {
    handlerClose();
  }
};

const handlerGoToSearchPage = () => {
  if (response?.value?.url) {
    location.href = response?.value?.url;
  }
};

const handlerMobileOpen = () => {
  if (screenWidth.value < 768) {
    if (!isMobileOpen.value) {
      isMobileOpen.value = true;
      return;
    }
  }
  handlerGoToSearchPage();
};

watch(
  () => isOpenResponse.value,
  (data) => {
    if (data) {
      document.addEventListener('click', handlerAnotherClick);
    } else {
      document.removeEventListener('click', handlerAnotherClick);
    }
  }
);

watch(
  () => screenWidth.value,
  () => {
    if (isMobileOpen.value && screenWidth.value >= 768) {
      isMobileOpen.value = false;
    }
  }
);
</script>

<style lang="scss" scoped>
.blog-search {
  position: relative;
  grid-area: search;
  @include point-md {
    align-self: flex-end;
  }
  &_active {
    display: flex;
    align-items: center;
    position: absolute;
    left: 52px;
    right: 16px;
    background-color: #fff;
    height: 60px;
  }
}
.blog-search-form {
  display: flex;
  align-items: center;
  margin-bottom: 0;
  @include point-md {
    border-bottom: 1px solid var(--background-black);
  }
  &__icon {
    width: 20px;
    height: 20px;
    fill: var(--background-black);
    @include point-md {
      margin-right: 16px;
    }
  }
  &__input {
    flex-grow: 1;
    height: 30px;
    border: unset;
    font-weight: 500;
    font-size: rem(16);
    line-height: 100%;
    color: var(--color-main);
    ::placeholder {
      color: #5f5f67;
    }
  }
  &__button {
    font-weight: 600;
    font-size: rem(12);
    text-transform: uppercase;
    background-color: transparent;
  }
  &__close {
    svg {
      display: block;
      width: 20px;
      height: 20px;
      fill: var(--background-red);
    }
  }
}
.blog-search-response {
  position: absolute;
  z-index: 2;
  padding: 16px;
  background-color: #f6f6f6;
  width: 100%;
  border: 1px solid #cacece;
  top: 100%;
  @include point-md {
    padding: 24px;
  }
  @include point-lg {
    padding: 24px 36px;
  }
  &__text {
  }
}
.blog-search-items {
  display: grid;
  grid-row-gap: 4px;
  max-height: 388px;
  overflow-y: auto;
}
</style>
